import React, { useCallback, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import { FormControl, FormGroup, Checkbox, FormControlLabel } from '@mui/material';
import { Controller, useFieldArray, useFormContext, useController, useWatch } from 'react-hook-form';
import { FormProps } from 'util/Form';
import ExplanationQuestionFormItem from 'components/applications/ApplicationQuestionsFormSection/ExplanationQuestionFormItem';
import { SelectQuestionOption } from 'store/types/SelectOption';

import commonStyles from 'styles/common.module.scss';
import styles from './ChecklistQuestionFormItem.module.scss';

interface ChecklistQuestionFormItemProps extends FormProps {
  fieldName: string;
  required?: boolean;
}

const ChecklistQuestionFormItem: React.FunctionComponent<ChecklistQuestionFormItemProps> = ({
  fieldName,
  required,
  disabled,
}) => {
  const { control, getValues, setValue, trigger, setError, clearErrors, formState } =
    useFormContext<Record<string, SelectQuestionOption[]>>();
  const { isSubmitted } = formState;
  const { fields } = useFieldArray({ control, name: fieldName });
  const options = useWatch({ control, name: fieldName }) as SelectQuestionOption[] | undefined;
  const validationMessage = 'Please select one or more options.';

  const validateCheckboxes = useCallback((items: SelectQuestionOption[] = []): boolean => {
    return items.some((item) => !!item?.value);
  }, []);

  const { fieldState: groupState } = useController({
    name: fieldName,
    control,
    rules: required
      ? {
          validate: (items: SelectQuestionOption[] = []) => validateCheckboxes(items) || validationMessage,
        }
      : undefined,
  });

  const handleChange = useCallback(
    (index: number) => (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      const current = getValues(fieldName) ?? [];
      const next = [...current];
      next[index] = { ...next[index], value: checked };
      setValue(fieldName, next, { shouldDirty: true });
      void trigger(fieldName);
    },
    [fieldName, getValues, setValue, trigger]
  );

  // - Only show the error after submit (or when RHF has marked it), but clear it as soon as there's a valid selection.
  // - This avoids showing an error on first render while still ensuring validation runs on submit.
  useEffect(() => {
    if (!required) {
      clearErrors(fieldName);
      return;
    }

    const hasOne = options?.some((item) => !!item?.value) ?? false;

    if (hasOne) {
      clearErrors(fieldName);
    } else if (isSubmitted) {
      setError(fieldName, { type: 'validate', message: validationMessage });
    }
  }, [options, isSubmitted, required, setError, clearErrors, fieldName]);

  return (
    <FormControl component={'fieldset'} required={required} disabled={disabled} fullWidth={true}>
      <FormGroup>
        {fields.map(({ id, name = '', includeExplanation }, index) => {
          const checked = options?.[index]?.value ?? false;

          return (
            <React.Fragment key={id}>
              <FormControlLabel
                label={name}
                control={
                  <Controller
                    name={`${fieldName}.${index}.value`}
                    control={control}
                    render={({ field: { ref, value } }) => {
                      return (
                        <Checkbox
                          color={'primary'}
                          size={'small'}
                          checked={!!value}
                          inputRef={ref}
                          onChange={handleChange(index)}
                        />
                      );
                    }}
                  />
                }
              />
              {includeExplanation && checked && (
                <div className={styles.explanationField}>
                  <ExplanationQuestionFormItem
                    parentFieldName={fieldName}
                    checkboxField={`${fieldName}.${index}.explanation`}
                    required={true}
                    defaultValue={options?.[index]?.explanation ?? ''}
                    disabled={disabled}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </FormGroup>

      {groupState.error && (
        <Alert severity={'error'} className={commonStyles.alert}>
          {groupState.error.message ?? validationMessage}
        </Alert>
      )}
    </FormControl>
  );
};
export default ChecklistQuestionFormItem;
